import React, { useEffect, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import TypingIndicator from './TypingIndicator';
import { getPlaceholderMessage } from '../utils/transcriptionStorage';

interface TranscriptionSegment {
  text: string;
  type: 'partial' | 'final';
  segment?: number;
  start?: number;
  end?: number;
  sentence_id?: number;
}

interface LiveTranscriptionProps {
  segments: TranscriptionSegment[];
  isTranscribing: boolean;
  language: string;
  fontSize: number;
  audioRef?: React.RefObject<HTMLAudioElement>;
  onTimecodeClick?: (time: number) => void;
}

function formatTime(seconds?: number): string | null {
  if (!seconds || seconds < 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

const getTimecodeStyles = (fontSize: number, isDark: boolean, onTimecodeClick?: (time: number) => void) => ({
  fontSize: fontSize * 0.65,
  fontWeight: 400,
  color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
  cursor: onTimecodeClick ? 'pointer' : 'default',
  textDecoration: 'none',
  fontFamily: 'monospace',
  mr: 0.5,
  opacity: 0.7,
  display: 'inline-block',
  verticalAlign: 'baseline',
  lineHeight: 0.8,
  '&:hover': onTimecodeClick ? {
    color: 'rgba(0, 207, 243, 0.6)',
    opacity: 1,
    textDecoration: 'underline'
  } : {}
});

const LiveTranscription: React.FC<LiveTranscriptionProps> = ({
  segments,
  isTranscribing,
  language,
  fontSize,
  audioRef,
  onTimecodeClick
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isRTL = language === 'he-IL';
  const hasContent = segments.length > 0 && segments.some(s => s.text.trim());
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timecodeStyles = getTimecodeStyles(fontSize, isDark, onTimecodeClick);

  useEffect(() => {
    if (containerRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      });
    }
  }, [segments]);

  if (!hasContent && !isTranscribing) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          minHeight: 200,
          textAlign: 'center',
          px: 3
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            fontSize: '1.1rem',
            fontStyle: 'italic'
          }}
        >
          {getPlaceholderMessage(language)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      dir={isRTL ? 'rtl' : 'ltr'}
      sx={{
        p: 3,
        flex: 1,
        overflow: 'auto',
        scrollBehavior: 'smooth',
        minHeight: 0,
        textAlign: isRTL ? 'right' : 'left'
      }}
    >
      {segments.length > 0 && (
        <Typography
          component="span"
          sx={timecodeStyles}
          onClick={() => onTimecodeClick?.(0)}
        >
          00:00
        </Typography>
      )}
      
      {segments.map((segment, index) => {
        const showTimecode = segment.type === 'final' && segment.start !== undefined;
        const timecode = formatTime(segment.start);

        return (
          <span key={index}>
            {showTimecode && timecode && (
              <Typography
                component="span"
                sx={timecodeStyles}
                onClick={() => {
                  if (segment.start !== undefined) {
                    onTimecodeClick?.(segment.start);
                  }
                }}
              >
                {timecode}
              </Typography>
            )}
            <Typography
              component="span"
              sx={{
                fontSize: fontSize,
                fontWeight: 500,
                lineHeight: 1.6,
                color: segment.type === 'final'
                  ? (isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)')
                  : (isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'),
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                letterSpacing: '0.01em',
                display: 'inline',
                whiteSpace: 'pre-line'
              }}
            >
              {segment.text}
              {index < segments.length - 1 && ' '}
            </Typography>
          </span>
        );
      })}
      
      {isTranscribing && (
        <Box component="span" sx={{ ml: 1 }}>
          <TypingIndicator />
        </Box>
      )}
      <div ref={scrollRef} />
    </Box>
  );
};

export default LiveTranscription;